import { extend, findIndex } from "@dhx/ts-common/core";
import { create, el } from "@dhx/ts-common/dom";
import { IEventSystem, EventSystem } from "@dhx/ts-common/events";
import { eventHandler, locate, locateNode } from "@dhx/ts-common/html";
import { Id, anyFunction, IHandlers, ITouchParam, SelectionEvents } from "@dhx/ts-common/types";
import { View } from "@dhx/ts-common/view";

import {
	dragManager,
	Selection,
	TreeCollection,
	DataEvents,
	DragEvents,
	IDataEventsHandlersMap,
	IDragEventsHandlersMap,
} from "@dhx/ts-data";

import { Editor, EditorEvents, IEditor, IEditorConfig } from "./Editor";
import { KeyManager, IKeyManager } from "@dhx/ts-common/KeyManager";
import { focusManager } from "@dhx/ts-common/FocusManager";
import {
	ITree,
	ITreeConfig,
	ITreeState,
	SelectStatus,
	TreeEvents,
	ITreeEventHandlersMap,
	ItemIcon,
	ITreeItem,
	Direction,
	IScrollConfig,
} from "./types";

function getSelectionIndent(level: number): number {
	return level * 20;
}

function getItemIconType(isFolder: boolean, isOpened: boolean): ItemIcon {
	if (!isFolder) {
		return "file";
	}
	if (isOpened) {
		return "openFolder";
	}
	return "folder";
}

export class Tree<T extends object = object> extends View implements ITree {
	public config: ITreeConfig;
	public data: TreeCollection<T & ITreeItem>;
	public events: IEventSystem<
		DataEvents | TreeEvents | DragEvents,
		IDataEventsHandlersMap & ITreeEventHandlersMap & IDragEventsHandlersMap
	>;
	public selection: Selection;

	private _editor: IEditor;
	private _handlers: IHandlers;
	private _isSelectionActive: boolean;
	private _root: Id;
	private _focusId: Id;
	private _right: number;
	private _keyManager: IKeyManager;

	private _touch: ITouchParam = {
		duration: 350,
		dblDuration: 300,
		timer: null,
		start: false,
		timeStamp: null,
	};

	private _scroll: IScrollConfig = {
		elem: null,
		step: 1,
		delay: 10,
	};

	private _isDraget: boolean;

	constructor(container: HTMLElement | string, config: ITreeConfig = {}) {
		super(container, config);
		this.config = extend(
			{
				dropBehaviour: "child",
				icon: {
					file: "dxi dxi-file-outline",
					folder: "dxi dxi-folder",
					openFolder: "dxi dxi-folder-open",
				},
				keyNavigation: true,
				editable: false,
				selection: true,
				rootId: (typeof container === "string" && container) || this._uid,
			},
			config
		);

		this.config.editable = this.config.editable || this.config.editing; // TODO: remove suite_7.0
		const init = (item: ITreeItem) => {
			item.$mark = SelectStatus.unselected;
			item.checkbox = this.config.checkbox;
			item.$autoload = Boolean(item.items && typeof this.config.autoload === "string");
			item.$editor = false;
			return item;
		};

		if (Array.isArray(this.config.data)) {
			this.events = new EventSystem<DataEvents>(this);
			this.data = new TreeCollection(
				{ autoload: this.config.autoload, init, rootId: this.config.rootId || container },
				this.events
			);
			this.data.parse(this.config.data);
		} else if (this.config.data && this.config.data.events) {
			this.data = this.config.data;
			this.data.config.init = init;
			this.events = this.data.events;
			this.events.context = this;
		} else {
			this.events = new EventSystem<DataEvents>(this);
			this.data = new TreeCollection(
				{ autoload: this.config.autoload, init, rootId: this.config.rootId || container },
				this.events
			);
		}

		this._isSelectionActive = true;
		this.selection = new Selection(
			{
				disabled: !this.config.selection,
			},
			this.data,
			this.events
		);

		if (this.config.keyNavigation) {
			this._keyManager = new KeyManager((_, focusId) => focusId === this._uid);
			this._initHotkeys();
		}
		this._editor = new Editor();

		this._initEvents();
		this._initHandlers();

		if (this.config.dragMode) {
			dragManager.setItem(this._uid, this);
		}
		this._root = this.data.getRoot();

		const render = () => this._draw();
		this.mount(container, create({ render }));
		focusManager.setFocusId(this._uid);
	}
	focusItem(id: Id): void {
		this._focusId = id;
		this.data.eachParent(id, item => {
			if (!item.opened) {
				this.expand(item.id);
			}
		});
		this.events.fire(TreeEvents.focusChange, [this.data.getIndex(id), id]);
		this.paint();
	}
	destructor(): void {
		this.events && this.events.clear();
		this._keyManager && this._keyManager.destructor();

		this.config = this.events = this.selection = null;
		this._editor = this._handlers = this._root = this._focusId = this._right = this._keyManager = this._touch = this._isDraget = null;

		this.unmount();
	}
	editItem(id: Id, config?: IEditorConfig): void {
		if (this._editor.isEditable()) {
			return;
		}
		const item = this.data.getItem(id);
		if (!this.events.fire(TreeEvents.beforeEditStart, [item.value, id])) return;
		this.data.update(
			id,
			{
				$edit: true,
				$editConfig: config,
			},
			true
		);
		this.events.fire(TreeEvents.afterEditStart, [item.value, id]);
	}
	getState(): ITreeState {
		const state: ITreeState = {};
		this.data.eachChild(
			this._root,
			item => {
				state[item.id] = {
					open: item.opened,
					selected: item.$mark,
				};
			},
			true
		);
		return state;
	}
	setState(state: ITreeState): void {
		this.data.eachChild(
			this._root,
			item => {
				if (item.id in state) {
					item.opened = state[item.id].open;
					item.$mark = state[item.id].selected;
				}
			},
			true
		);
		this.paint();
	}
	toggle(id: Id): void {
		const item = this.data.getItem(id);
		if (item.$autoload) {
			if (!this.events.fire(TreeEvents.beforeExpand, [id])) {
				return;
			}
			this.data.loadItems(id);
			this.data.update(id, {
				$autoload: false,
				opened: true,
			});
			this.events.fire(TreeEvents.afterExpand, [id]);
		} else {
			if (item.opened) {
				this.collapse(id);
			} else {
				this.expand(id);
			}
		}
	}
	getChecked(): Id[] {
		const checked = [];
		this.data.eachChild(this._root, item => {
			if (item.$mark === SelectStatus.selected) {
				checked.push(item.id);
			}
		});
		return checked;
	}
	checkItem(id: Id): void {
		if (!id || !this.data.getItem(id)) return;
		this._updateItemCheck(id, SelectStatus.selected);
		this.paint();
	}
	collapse(id: Id): void {
		if (this.data.haveItems(id)) {
			if (!this.events.fire(TreeEvents.beforeCollapse, [id])) {
				return;
			}
			this.data.update(id, { opened: false });
			this.events.fire(TreeEvents.afterCollapse, [id]);
		}
	}
	collapseAll(): void {
		this.data.eachChild(this._root, ({ id }) => this.collapse(id), true);
	}
	expand(id: Id): void {
		if (this.data.haveItems(id)) {
			if (!this.events.fire(TreeEvents.beforeExpand, [id])) {
				return;
			}
			this.data.update(id, { opened: true });
			this.events.fire(TreeEvents.afterExpand, [id]);
		}
	}
	expandAll(): void {
		this.data.eachChild(this._root, ({ id }) => this.expand(id), true);
	}
	uncheckItem(id: Id): void {
		if (!id || !this.data.getItem(id)) return;
		this._updateItemCheck(id, SelectStatus.unselected);
		this.paint();
	}
	/** @deprecated See a documentation: https://docs.dhtmlx.com/ */
	close(id: Id): void {
		this.collapse(id);
	}
	/** @deprecated See a documentation: https://docs.dhtmlx.com/ */
	closeAll(): void {
		this.collapseAll();
	}
	/** @deprecated See a documentation: https://docs.dhtmlx.com/ */
	open(id: Id): void {
		this.expand(id);
	}
	/** @deprecated See a documentation: https://docs.dhtmlx.com/ */
	openAll(): void {
		this.expandAll();
	}
	/** @deprecated See a documentation: https://docs.dhtmlx.com/ */
	unCheckItem(id: Id): void {
		this.uncheckItem(id);
	}
	private _draw() {
		this._getRightPos();
		const items = this._drawItems(this.data.getRoot());
		return el(
			"ul",
			{
				class:
					"dhx_widget dhx_tree" +
					(!this._isSelectionActive ? " dhx_tree--no-selection " : "") +
					(this.config.css ? " " + this.config.css : ""),
				"data-dhx-widget-id": this._uid,
				tabindex: 0,
				"data-dhx-root-id": this.config.rootId,
				...this._handlers,
			},
			items
		);
	}
	private _initEvents() {
		this.data.events.on(DataEvents.change, (id, status, obj) => {
			if (status === "remove") {
				this._updateParents(obj.parent, true);
			}
			if (status === "add") {
				this._updateParents(id);
			}
			this.paint();
		});

		this.data.events.on(DataEvents.beforeRemove, ({ id, parent }) => {
			if (id != this._focusId) {
				return;
			}
			const childs = this.data.findAll(i => i.parent === parent);
			let nextId = parent;
			if (childs.length > 1) {
				const currentIndex = childs.findIndex(i => i.id === id);
				const nextIndex = currentIndex === childs.length - 1 ? childs.length - 2 : currentIndex + 1;
				nextId = childs[nextIndex].id;
			}
			this.selection.remove(id);
			this.selection.add(nextId);
			this.focusItem(nextId);
		});

		this._editor.events.on(EditorEvents.begin, (id: Id) => {
			if (id === this._uid) {
				this.config.$editable = true;
			}
		});
		this._editor.events.on(EditorEvents.end, (target, id, value) => {
			if (!this.events.fire(TreeEvents.beforeEditEnd, [value, id])) {
				return false;
			}
			if (this._uid === target) {
				this.data.update(id, { $edit: false, value });
			}
			this.config.$editable = false;
			this.events.fire(TreeEvents.afterEditEnd, [value, id]);
		});

		this.events.on(DragEvents.beforeDrag, ({ start }, _events, ghost) => {
			const item = this.data.getItem(start);
			const isFolder = this.config.isFolder ? this.config.isFolder(item) : this.data.haveItems(item.id);
			const iconType = getItemIconType(isFolder, item.opened);
			const icon = (item.icon || this.config.icon)[iconType] || this.config.icon[iconType];
			ghost.innerHTML = `<div class="dhx_tree-list-item__icon ${icon}"></div><span class="dhx_tree-list-item__text">${item.text ||
				item.value}</span>`;
		});
		this.events.on(DragEvents.canDrop, ({ target, dropPosition }) => {
			let dropStatus;
			if (this.config.dropBehaviour === "complex") {
				if (dropPosition === "top") {
					dropStatus = "dhx_tree-drop--top";
				} else if (dropPosition === "bottom") {
					dropStatus = "dhx_tree-drop--bottom";
				} else {
					dropStatus = "dhx_tree-drop--in-folder";
				}
			} else {
				dropStatus =
					this.config.dropBehaviour === "child"
						? "dhx_tree-drop--in-folder"
						: "dhx_tree-drop--bottom";
			}
			if (this.data.exists(target)) {
				this.data.update(target, { $drophere: dropStatus }, true);
			}
			this.paint();
		});
		this.events.on(DragEvents.cancelDrop, ({ target }) => {
			if (this.data.exists(target)) {
				this.data.update(target, { $drophere: null }, true);
			}
		});
		this.events.on(DragEvents.afterDrop, ({ target, dropPosition }) => {
			if (
				this.config.dropBehaviour === "child" ||
				(this.config.dropBehaviour === "complex" && dropPosition === "in")
			) {
				this.expand(target);
			}
		});
		this.events.on(DragEvents.dragStart, (data, e) => {
			this._scroll.elem = locateNode(e, "data-dhx-root-id");
			this._autoScroll(this._scroll);
			this._isSelectionActive = false;
			this.paint();
		});
		this.events.on(DragEvents.afterDrag, drag => {
			this._isSelectionActive = true;
			this._isDraget = true;
			if (this.data.exists(drag.start)) {
				this.selection.add(drag.start);
			}
			this.paint();
		});
		this.events.on(TreeEvents.itemClick, id => {
			this._focusId = id;
			this.paint();
		});
		this.events.on(SelectionEvents.afterSelect, () => this.paint());
		this.events.on(SelectionEvents.afterUnSelect, () => this.paint());
	}
	private _autoScroll(config: IScrollConfig): void {
		const elem = config.elem;
		if (elem) {
			const elemRect = elem.getBoundingClientRect();
			const { top: elemTop, bottom: elemBottom, height: elemHeight } = elemRect;
			const elemScrollHeight = elem.scrollHeight;
			let timerId = null;

			const scroll = config => {
				const { elem, step, delay, direction } = config;
				return setInterval(() => {
					elem.scrollBy(0, direction === Direction.up ? -step : step);
				}, delay);
			};

			const handler = e => {
				const mousePosition = e.clientY;
				if (mousePosition > elemBottom) {
					if (!timerId) {
						timerId = scroll({ ...config, direction: Direction.down });
					}
				} else if (mousePosition < elemTop) {
					if (!timerId) {
						timerId = scroll({ ...config, direction: Direction.up });
					}
				} else if (timerId) {
					clearInterval(timerId);
					timerId = null;
				}
			};

			if (elemHeight < elemScrollHeight) {
				document.addEventListener("pointermove", handler);
				document.addEventListener(
					"pointerup",
					() => {
						document.removeEventListener("pointermove", handler);
						clearInterval(timerId);
						timerId = null;
					},
					{ once: true }
				);
			}
		}
	}
	private _initHandlers() {
		this._handlers = {
			onmouseleave: e => {
				dragManager.cancelCanDrop(e);
			},
			onclick: e => {
				if (this._isDraget) {
					this._isDraget = false;
					return;
				}
				const id = locate(e);
				if (!id) {
					return;
				}
				if (e.target.classList.contains("dhx_tree-toggle-button")) {
					this.toggle(id);
					return;
				}
				if (e.target.classList.contains("dhx_tree-checkbox")) {
					if (!this.events.fire(TreeEvents.beforeCheck, [this.data.getIndex(id), id])) {
						return;
					}
					const item = this.data.getItem(id);
					if (item.$mark === SelectStatus.unselected) {
						this.checkItem(id);
					} else {
						this.uncheckItem(id);
					}
					this.events.fire(TreeEvents.afterCheck, [this.data.getIndex(id), id, !!item.$mark]);
					return;
				}
				this.events.fire(TreeEvents.itemClick, [id, e]);
				if (this.data.exists(id)) {
					this.selection.add(id);
				}
			},
			ondblclick: (e: MouseEvent) => {
				this._dblClick(e);
			},
			ondragstart(e) {
				e.preventDefault();
			},
			onmousedown: (e: MouseEvent) => {
				this._dragStart(e);
			},
			ontouchstart: (e: TouchEvent) => {
				this._touch.timer = setTimeout(() => {
					this._dragStart(e);
				}, this._touch.duration);

				if (!this._touch.timeStamp) {
					this._touch.timeStamp = +e.timeStamp.toFixed();
				} else {
					const doubleTap =
						this._touch.dblDuration >= this._touch.timeStamp - +e.timeStamp.toFixed();
					if (doubleTap) {
						this._dblClick(e);
					}
					this._touch.timeStamp = null;
				}
				setTimeout(() => {
					this._touch.timeStamp = null;
				}, this._touch.dblDuration);
			},
			ontouchmove: (e: TouchEvent) => {
				this._touch.start && e.preventDefault();
				this._clearTouchTimer();
			},
			ontouchend: () => {
				this._touch.start = false;
				this._clearTouchTimer();
			},
			oncontextmenu: e => {
				const id = locate(e);
				if (!id) {
					return;
				}
				this.events.fire(TreeEvents.itemRightClick, [id, e]);
				this.events.fire(TreeEvents.itemContextMenu, [id, e]); // TODO: remove suite_7.0
			},
		};

		const evs = this.config.eventHandlers;
		if (evs) {
			for (const [event_name, events] of Object.entries(evs)) {
				this._handlers[event_name] = eventHandler(
					event => {
						const item = { ...this.data.getItem(locate(event)) };
						Object.keys(item).forEach(key => {
							if (key.startsWith("$")) {
								delete item[key];
							}
						});
						return item;
					},
					events,
					this._handlers[event_name] as anyFunction
				);
			}
		}
	}
	private _dblClick(e: MouseEvent | TouchEvent): void {
		const id = locate(e);
		if (!id) {
			return;
		}
		this.events.fire(TreeEvents.itemDblClick, [id, e]);

		if (this.config.editable) {
			this.editItem(id);
		}
	}
	private _clearTouchTimer(): void {
		if (this._touch.timer) {
			clearTimeout(this._touch.timer);
			this._touch.timer = null;
		}
	}
	private _dragStart(event: MouseEvent | TouchEvent): void {
		if (this.config.dragMode && this.config.dragMode !== "target") {
			if (this._editor.isEditable()) {
				return;
			}
			this._touch.start = true;
			dragManager.onMouseDown(event);
		}
	}

	private _getRightPos(id?: Id, level = 0) {
		if (!id) {
			const root = this.data.getRoot();
			const items = this.data.findAll(i => i.parent === root);
			for (const item of items) {
				return this._getRightPos(item.id, level);
			}
		}
		const item = this.data.getItem(id);
		if (!item) {
			return;
		}
		this._right = level;

		if (this.data.haveItems(item.id) && item.opened && item.items) {
			for (const child of item.items) {
				this._getRightPos(child.id, level + 1);
			}
		}
	}

	private _drawItems(parent, level = 0) {
		const order = this.data.getRawData(0, -1, null, 0, parent);
		return order.map(item => {
			if (!item) {
				return;
			}
			let toggleButton;
			let childs;
			let select;
			let isFolder = this.config.isFolder ? this.config.isFolder(item) : false;

			if (item.$autoload || this.data.haveItems(item.id)) {
				isFolder = true;
				toggleButton = el("div", { class: "dxi dxi-menu-right dhx_tree-toggle-button" }, "");
				if (item.opened) {
					toggleButton = el(
						"div",
						{
							class: "dxi dxi-menu-down dhx_tree-toggle-button dhx_tree-toggle-button--open",
						},
						""
					);
					childs = this._drawItems(item.id, level + 1);
				}
			}

			if (item.checkbox) {
				const className =
					item.$mark === SelectStatus.indeterminate
						? "dxi-minus-box"
						: item.$mark === SelectStatus.selected
						? "dxi-checkbox-marked"
						: "dxi-checkbox-blank-outline";
				select = el("div", {
					class: "dhx_tree-checkbox dxi " + className,
				});
			}
			const iconType = getItemIconType(isFolder, item.opened);
			const icon = (item.icon || this.config.icon)[iconType] || this.config.icon[iconType];

			let itemValue;
			if (item.$edit) {
				itemValue = this._editor.edit(this._uid, {
					item,
					...item.$editConfig,
				});
			} else {
				itemValue = el("span", { class: "dhx_tree-list-item__text" }, item.text || item.value);
			}
			if (this.config.template) {
				const template = this.config.template(item, isFolder);
				itemValue =
					typeof template === "string"
						? el("div.dhx_tree-template__wrapper", {
								".innerHTML": template,
						  })
						: itemValue;
			}
			const itemHeight = this.config.itemHeight || null;

			if (isFolder) {
				return el(
					"li",
					{
						class:
							"dhx_tree-list-item dhx_tree-list-item--parent" +
							(item.css ? " " + item.css : ""),
						"data-dhx-id": item.id,
						_key: item.id,
					},
					[
						el(
							"div.dhx_tree-folder",
							{
								class:
									(item.id == this._focusId ? " dhx_tree-folder--focused" : "") +
									(item.$selected ? " dhx_tree-folder--selected" : "") +
									(item.$drophere ? " " + item.$drophere : ""),
								style: {
									left: `${-getSelectionIndent(level)}px`,
									right: 0,
									"margin-left": `${getSelectionIndent(level)}px`,
									minHeight: itemHeight,
								},
							},
							[
								toggleButton,
								el("div.dhx_tree-list-item__content", [
									select,
									el("div", { class: "dhx_tree-list-item__icon " + icon }),
									itemValue,
								]),
							]
						),
						childs && el("ul.dhx_tree-list", childs),
					]
				);
			} else {
				return el(
					"li",
					{
						class:
							"dhx_tree-list__item dhx_tree-list-item" +
							(item.id == this._focusId ? " dhx_tree-list-item--focused" : "") +
							(item.$selected ? " dhx_tree-list-item--selected" : "") +
							(item.$drophere ? " " + item.$drophere : "") +
							(isFolder ? "dhx_tree-list-item--folder" : "") +
							(item.css ? " " + item.css : ""),
						style: {
							left: `${-getSelectionIndent(level)}px`,
							right: 0,
							"margin-left": `${getSelectionIndent(level)}px`,
							minHeight: itemHeight,
						},
						"data-dhx-id": item.id,
						level,
					},
					[
						toggleButton,
						el(
							"div",
							{
								class: "dhx_tree-list-item__content",
							},
							[select, el("div", { class: "dhx_tree-list-item__icon " + icon }), itemValue]
						),
					]
				);
			}
		});
	}
	private _updateItemCheck(id: Id, status: SelectStatus) {
		this.data.update(id, { $mark: status }, true);
		this.data.eachChild(id, obj => (obj.$mark = status)); // mark all childs
		// this.events.fire(TreeEvents.afterCheck, [id]);
		this._updateParents(id);
	}
	private _updateParents(id: Id, self = false) {
		if (id === this._root) {
			return;
		}
		this.data.eachParent(
			id,
			obj => {
				let selected = 0;
				let unselected = 0;
				this.data.eachChild(
					obj.id,
					child => {
						if (!child.checkbox) {
							return;
						}
						switch (child.$mark) {
							case SelectStatus.unselected:
								unselected++;
								break;
							case SelectStatus.selected:
								selected++;
								break;
							default:
								break;
						}
					},
					true
				);
				if (selected !== 0 && unselected !== 0) {
					obj.$mark = SelectStatus.indeterminate;
				} else if (selected === 0 && unselected !== 0) {
					obj.$mark = SelectStatus.unselected;
				} else {
					obj.$mark = SelectStatus.selected;
				}
			},
			self
		);
	}
	private _initHotkeys() {
		const getFocused = () => {
			const focused = this._focusId;
			if (focused) {
				return focused;
			} else {
				const root = this.data.getRoot();
				return this.data.getItems(root)[0].id;
			}
		};

		const getClosestBot = (id, ignore = false) => {
			const item = this.data.getItem(id);
			const haveItems = this.data.haveItems(id);
			if (haveItems && item.opened && !ignore) {
				return this.data.getItems(id)[0].id;
			} else {
				const parent = this.data.getParent(id);
				const childs = this.data.getItems(parent);
				const index = findIndex(childs, child => child.id === id);
				const len = childs.length;
				if (index + 1 < len) {
					return childs[index + 1].id;
				} else {
					if (parent === this.data.getRoot()) {
						return null;
					}
					return getClosestBot(parent, true);
				}
			}
		};

		const getClosestTop = (id: Id) => {
			const index = this.data.getIndex(id);
			const parent = this.data.getParent(id);

			if (index > 0) {
				const childs = this.data.getItems(parent);
				let current = childs[index - 1];
				if (!this.data.haveItems(current.id) || !current.opened) {
					return current.id;
				}

				while (this.data.haveItems(current.id) && current.opened) {
					const allChilds = this.data.getItems(current.id);
					current = allChilds[allChilds.length - 1];
				}
				return current.id;
			} else {
				if (parent === this.data.getRoot()) {
					return null;
				}
				return parent;
			}
		};

		const handlers = {
			arrowLeft: e => {
				e.preventDefault();
				const focused = getFocused();
				const parent = this.data.getParent(focused);

				if (this.data.getRoot() === parent) {
					this.collapse(focused);
					return;
				}
				const isClosed = !this.data.getItem(focused).opened;

				if (isClosed) {
					this.focusItem(parent);
				} else {
					if (focused !== this.data.getRoot()) {
						this.collapse(focused);
					}
				}
			},
			arrowRight: e => {
				e.preventDefault();
				const focused = getFocused();
				const item = this.data.getItem(focused);
				if (item.$autoload) {
					if (!this.events.fire(TreeEvents.beforeExpand, [focused])) {
						return;
					}
					this.data.loadItems(focused);
					this.data.update(focused, {
						$autoload: false,
						opened: true,
					});
					this.events.fire(TreeEvents.afterExpand, [focused]);
					return;
				}
				if (this.data.haveItems(focused)) {
					this.expand(focused);
				}
			},
			arrowUp: e => {
				e.preventDefault();
				const focused = getFocused();
				const next = getClosestTop(focused);
				if (next) {
					this.focusItem(next);
				}
			},
			arrowDown: e => {
				e.preventDefault();
				const focused = getFocused();
				const next = getClosestBot(focused);
				if (next) {
					this.focusItem(next);
				}
			},
			enter: () => {
				const focused = getFocused();
				if (focused) {
					this.selection.add(focused);
				}
			},
		};

		for (const key in handlers) {
			this._keyManager.addHotKey(key, handlers[key]);
		}
	}
}
